import BN from "bn.js";
import { PublicKey } from "@solana/web3.js";
import { TxVersion } from "@raydium-io/raydium-sdk-v2";
import { IDX_LAUNCHLAB_PLATFORM_SETTINGS } from "@/config/idxLaunchlabLandingConfig";
import {
  IDX_LAUNCHLAB_DEFAULT_CONFIG_ID_MAINNET,
  getIdxLaunchlabPlatformPda,
} from "@/config/idxLaunchlabAppConfig";
import { getLaunchlabCluster } from "@/lib/idx-launchlab/constants";
import { IDX_LAUNCHLAB_ON_CHAIN_CURVE } from "@/lib/idx-launchlab/platform-on-chain-config";

/** Raydium JustSendit-style defaults (1B supply, 80% curve, 85 SOL target). */
export const LAUNCHLAB_DEFAULT_SUPPLY = new BN(IDX_LAUNCHLAB_ON_CHAIN_CURVE.supply);
export const LAUNCHLAB_DEFAULT_TOTAL_SELL_A = new BN(IDX_LAUNCHLAB_ON_CHAIN_CURVE.totalSellA);
export const LAUNCHLAB_DEFAULT_TOTAL_FUND_RAISING_B = new BN(
  IDX_LAUNCHLAB_ON_CHAIN_CURVE.totalFundRaisingB,
);

/** Default 1% slippage for create+buy (matches LaunchlabTradePanel). */
export const LAUNCHLAB_DEFAULT_SLIPPAGE_BPS = new BN(100);

/**
 * Build args for raydium.launchpad.createLaunchpad().
 * @param {{ mintA: PublicKey; name: string; symbol: string; uri: string; buyAmountLamports?: BN; createOnly?: boolean; slippage?: BN; configId?: PublicKey; platformId?: PublicKey }} input
 */
export function buildCreateLaunchpadParams(input) {
  const platformPda = input.platformId ?? getIdxLaunchlabPlatformPda();
  if (!platformPda) {
    throw new Error("IDX Launchlab platform PDA is not configured.");
  }

  const configIdStr =
    getLaunchlabCluster() === "devnet"
      ? process.env.NEXT_PUBLIC_IDX_LAUNCHLAB_CONFIG_ID?.trim()
      : IDX_LAUNCHLAB_DEFAULT_CONFIG_ID_MAINNET;

  if (!configIdStr) {
    throw new Error("LaunchLab GlobalConfig id is not configured for this cluster.");
  }

  const settings = IDX_LAUNCHLAB_PLATFORM_SETTINGS;
  const buyAmount = input.buyAmountLamports ?? new BN(0);
  const createOnly = input.createOnly ?? buyAmount.isZero();

  return {
    mintA: input.mintA,
    name: input.name.trim(),
    symbol: input.symbol.trim(),
    uri: input.uri.trim(),
    decimals: settings.decimals,
    migrateType: "cpmm",
    configId: input.configId ?? new PublicKey(configIdStr),
    platformId: new PublicKey(platformPda),
    supply: LAUNCHLAB_DEFAULT_SUPPLY,
    totalSellA: LAUNCHLAB_DEFAULT_TOTAL_SELL_A,
    totalFundRaisingB: LAUNCHLAB_DEFAULT_TOTAL_FUND_RAISING_B,
    totalLockedAmount: new BN(0),
    buyAmount,
    createOnly,
    slippage: input.slippage ?? LAUNCHLAB_DEFAULT_SLIPPAGE_BPS,
    token2022: true,
    txVersion: TxVersion.V0,
  };
}

export function getLaunchPresetSummary() {
  const settings = IDX_LAUNCHLAB_PLATFORM_SETTINGS;
  return {
    supplyHuman: settings.supplyHuman,
    decimals: settings.decimals,
    graduationFractionPercent: settings.graduationFractionBps / 100,
    quoteSymbol: settings.quoteSymbol,
    curveTypeLabel: "Constant product (Pump-style)",
    graduationTargetSol: IDX_LAUNCHLAB_ON_CHAIN_CURVE.graduationTargetSol,
    platformFeePercent: settings.fees.platformRatePerMillion / 10_000,
    creatorFeePercent: settings.fees.creatorRatePerMillion / 10_000,
    lpSplit: settings.lpSplit,
  };
}
